import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Box, Button, Typography, TextField, InputLabel } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { IFailureCodeValues, IFailureCodeForm } from "./FailureTypeCreationFormInterfaces";
import { failureCodeFormValidationSchema } from "validations/schemas";
import { useStyles } from "./FailureCodeCreationForm.styles";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { FailureDetailsSettingsContext } from "contexts/FailureDetailsSettings/FailureDetailsSettingsContext";

export const FailureCodeCreationForm = ({ handleClose, corporateId }: IFailureCodeForm) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { apolloMutationError, addFailureCode } = FailureDetailsEntity.InsertFailureCode();
    const { selectedFailureTypeId, selectedFailureCodeId } = useContext(FailureDetailsSettingsContext);

    const handleAddFailureCodeSubmit = useCallback(
        async (values: IFailureCodeValues) => {
            const failureCodeValues = {
                name: values.name,
                primary_code_id: selectedFailureCodeId ?? null,
                corporate_id: corporateId,
                failure_type_id: selectedFailureTypeId,
            };

            try {
                const response = await addFailureCode({
                    variables: { failure_code: failureCodeValues },
                });

                if (response) {
                    handleClose();
                }
            } catch {
                return;
            }
        },
        [addFailureCode, handleClose, corporateId, selectedFailureTypeId, selectedFailureCodeId],
    );

    const initFormValues: IFailureCodeValues = {
        name: "",
        primary_code_id: selectedFailureCodeId ?? null,
        corporate_id: corporateId,
        failure_type_id: selectedFailureTypeId!,
    };

    return (
        <Formik
            initialValues={initFormValues}
            validationSchema={failureCodeFormValidationSchema}
            onSubmit={handleAddFailureCodeSubmit}
        >
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                return (
                    <Form className={classes.formContainer}>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-failure-code-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t("failure_details_setup.add_failure_types_modal.name")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-failure-code-name-label"
                                fullWidth
                                id="outlined-failure-code-name-input"
                                className={classes.textField}
                                name="name"
                                type="text"
                                variant="outlined"
                                autoFocus={true}
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name && t(errors.name)}
                            />
                        </Box>
                        <p
                            style={{ display: apolloMutationError ? "block" : "none", color: "red" }}
                            className={classes.errorText}
                        >
                            {apolloMutationError?.message.includes("uq_corporate_failure_code_name")
                                ? t("failure_details_setup.add_failure_codes_modal.existing_failure_code_name_error")
                                : apolloMutationError?.message.includes("uq_corporate_secondary_failure_code_name")
                                ? t(
                                      "failure_details_setup.add_secondary_failure_codes_modal.existing_secondary_failure_code_name_error",
                                  )
                                : apolloMutationError?.message.includes(
                                      "check constraint of an insert/update permission has failed",
                                  )
                                ? t("failure_details_setup.add_failure_codes_modal.no_corporate_id_failure_code_error")
                                : t("")}
                        </p>
                        <div className={classes.buttonsContainer}>
                            <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                                {t("failure_details_setup.add_failure_codes_modal.cancel")}
                            </Button>
                            <Button
                                className={classes.addButton}
                                variant="outlined"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                <AddIcon /> {t("failure_details_setup.add_failure_codes_modal.save")}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
